import { TranslateService } from '@ngx-translate/core';
import { ActivityTypes } from '../../../shared/model/enum/activity-types';
import { FilterCardOptionInterface } from '../../../shared/component/filter/filter.class';
import { EFilterDropdownElements } from '../../../store/filter/filter.model';
import { DropdownComponent } from '../../../shared/component/filter/dropdown/dropdown.component';
import { CodeAndNameDisplayService } from '../../../shared/service/code-and-name-display/code-and-name-display.service';
import { Line, Site } from '../../../shared/component/filter/filterable-objects.class';
import { CustomDependFilterKeysEnum } from '../../../shared/service/work-order-schedule/work-order-schedule.model';
import {
  ETagType,
  IApiAutomationOption,
  TAG_COMBINATION_DESTINATION_RESOURCE,
} from '../../../store/settings/api-automation/shared/api-automation.model';

const TRANSLATION_PREFIX: string = 'settings.apiAutomationSettings';
const FILTER_CARD_SCSS: string = 'col-md-3 m-t-5 m-b-3';

/**
 * A tag combination is evaluated server-side by substituting each bare number with the matching tag
 * definition's cached state value (quoted), stripping the `~` marker from literal numbers, mapping
 * AND/OR/EQUALS/NOT_EQUALS onto JS operators and running the result. A malformed expression is only
 * discovered at evaluation time, where it is logged as a syntax error and the rule silently never
 * fires, so the shape is checked here before it can be saved.
 */
export enum ETagCombinationSyntaxError {
  none = 'none',
  unknownToken = 'unknownToken',
  unbalancedParentheses = 'unbalancedParentheses',
  malformedExpression = 'malformedExpression',
  noTagReference = 'noTagReference',
}

export enum EExtendedMode {
  no = 0,
  changeActivityAndWait = 1,
  doNotChangeActivityAndWait = 2,
}

export class ApiAutomationSettingsUtilities {
  private static readonly codeAndNameDisplayService: CodeAndNameDisplayService = new CodeAndNameDisplayService();

  /**
   * Only tag definitions and burst settings still carry site/line context, so only those two
   * screens get the site/line filter card.
   */
  public static getSiteLineFilterOptions(translate: TranslateService): FilterCardOptionInterface {
    return {
      rows: [
        [
          {
            elementId: EFilterDropdownElements.siteMultiSelectDropdown,
            type: DropdownComponent,
            cls: FILTER_CARD_SCSS,
            object: Site,
            outputOptions: {
              filterObjectId: CustomDependFilterKeysEnum.SITE_ID,
              filterObjectProp: 'id',
              returnFilterObjectAllProp: false,
            },
            options: {
              isRequired: false,
              text: translate.instant('filterCard.site.dropdownPlaceHolder'),
              getInitData: true,
            },
          },
          {
            elementId: EFilterDropdownElements.lineMultiSelectDropdown,
            type: DropdownComponent,
            cls: FILTER_CARD_SCSS,
            object: Line,
            outputOptions: {
              filterObjectId: CustomDependFilterKeysEnum.LINE_IDS,
              filterObjectProp: 'id',
              returnFilterObjectAllProp: false,
            },
            options: {
              isRequired: false,
              text: translate.instant('filterCard.line.dropdownPlaceHolder'),
              getInitData: true,
            },
          },
        ],
      ],
    };
  }

  public static getValueOperatorOptions(translate: TranslateService): IApiAutomationOption[] {
    return ['any', 'one_of', 'not_one_of', 'between'].map((operator) => ({
      id: operator,
      name: translate.instant(`${TRANSLATION_PREFIX}.modal.valueTypeOperator.${operator}`),
    }));
  }

  /** The nine tag types the API validates `tag_definitions.tagType` against. */
  public static getTagTypeOptions(translate: TranslateService): IApiAutomationOption[] {
    return Object.values(ETagType).map((tagType) => ({
      id: tagType,
      name: translate.instant(`${TRANSLATION_PREFIX}.modal.tagType.${tagType}`),
    }));
  }

  /**
   * A tag combination can only target change_activity or change_station_activity, so its tag type
   * list is derived from that mapping rather than the full nine — keeping the dropdown and the
   * destination lookup from drifting apart.
   */
  public static getTagCombinationTagTypeOptions(translate: TranslateService): IApiAutomationOption[] {
    return Object.keys(TAG_COMBINATION_DESTINATION_RESOURCE).map((tagType) => ({
      id: tagType,
      name: translate.instant(`${TRANSLATION_PREFIX}.modal.tagType.${tagType}`),
    }));
  }

  /**
   * Matches one token: a grouping paren, a logical/comparison keyword, a tag definition id, or a
   * `~`-prefixed literal number. The keywords are anchored on a word boundary so `ANDY` is not read
   * as `AND` followed by rubbish, and NOT_EQUALS is tried before EQUALS.
   */
  private static readonly TAG_COMBINATION_TOKEN: RegExp = /\s*(\(|\)|AND\b|OR\b|NOT_EQUALS\b|EQUALS\b|~?\d+)/gy;

  private static readonly TAG_COMBINATION_OPERATORS: ReadonlySet<string> = new Set([
    'AND',
    'OR',
    'EQUALS',
    'NOT_EQUALS',
  ]);

  /**
   * Splits an expression into tokens, returning null as soon as something unrecognised appears.
   */
  private static tokenizeTagCombination(value: string): string[] | null {
    const expression: string = value.trim();
    const pattern: RegExp = new RegExp(
      ApiAutomationSettingsUtilities.TAG_COMBINATION_TOKEN.source,
      ApiAutomationSettingsUtilities.TAG_COMBINATION_TOKEN.flags,
    );
    const tokens: string[] = [];
    let match: RegExpExecArray | null;
    let consumed: number = 0;

    while ((match = pattern.exec(expression)) !== null) {
      tokens.push(match[1]);
      // exec resets lastIndex once it stops matching, so the position is tracked here
      consumed = pattern.lastIndex;
    }

    // a sticky match stops at the first unrecognised character; anything left over is invalid
    return expression.slice(consumed).trim() === '' ? tokens : null;
  }

  /**
   * Returns the first structural problem with a tag combination, or `none` when it would evaluate.
   * An empty value is treated as valid so the required rule owns that message.
   */
  public static getTagCombinationSyntaxError(value: string): ETagCombinationSyntaxError {
    const expression: string = (value ?? '').trim();

    if (!expression) {
      return ETagCombinationSyntaxError.none;
    }

    const tokens: string[] | null = ApiAutomationSettingsUtilities.tokenizeTagCombination(expression);

    if (tokens === null || !tokens.length) {
      return ETagCombinationSyntaxError.unknownToken;
    }

    let depth: number = 0;
    let expectOperand: boolean = true;

    for (const token of tokens) {
      if (token === '(') {
        if (!expectOperand) {
          return ETagCombinationSyntaxError.malformedExpression;
        }

        depth += 1;
      } else if (token === ')') {
        // closing while an operand is still owed means an empty group or a dangling operator
        if (expectOperand) {
          return ETagCombinationSyntaxError.malformedExpression;
        }

        depth -= 1;

        if (depth < 0) {
          return ETagCombinationSyntaxError.unbalancedParentheses;
        }
      } else if (ApiAutomationSettingsUtilities.TAG_COMBINATION_OPERATORS.has(token)) {
        if (expectOperand) {
          return ETagCombinationSyntaxError.malformedExpression;
        }

        expectOperand = true;
      } else {
        if (!expectOperand) {
          return ETagCombinationSyntaxError.malformedExpression;
        }

        expectOperand = false;
      }
    }

    if (depth !== 0) {
      return ETagCombinationSyntaxError.unbalancedParentheses;
    }

    if (expectOperand) {
      return ETagCombinationSyntaxError.malformedExpression;
    }

    // a `~`-prefixed number is a literal; without a bare id the rule can never match a tag
    if (!tokens.some((token) => /^\d+$/.test(token))) {
      return ETagCombinationSyntaxError.noTagReference;
    }

    return ETagCombinationSyntaxError.none;
  }

  /** True unless the expression fails with this specific problem, so each rule shows its own message. */
  public static hasNoTagCombinationError(value: string, error: ETagCombinationSyntaxError): boolean {
    return ApiAutomationSettingsUtilities.getTagCombinationSyntaxError(value) !== error;
  }

  /**
   * The JSON columns are edited as text, so the form validates the shape before the payload goes
   * out: `array` accepts any JSON array, `number`/`string` an array of that element type, `integer`
   * an array of whole numbers and `object` a JSON object or array.
   */
  public static isValidJson(value: string, shape: 'array' | 'number' | 'integer' | 'string' | 'object'): boolean {
    const trimmed: string = (value ?? '').trim();

    if (!trimmed) {
      return true;
    }

    let parsed: unknown;

    try {
      parsed = JSON.parse(trimmed);
    } catch {
      return false;
    }

    if (shape === 'object') {
      return typeof parsed === 'object' && parsed !== null;
    }

    if (!Array.isArray(parsed)) {
      return false;
    }

    if (shape === 'integer') {
      return parsed.every((item) => Number.isInteger(item));
    }

    return shape === 'array' || parsed.every((item) => typeof item === shape);
  }

  /**
   * Reads an id-array column (`changePreventiveTasks`, `monitorActivityIds`, ...) that the API may
   * return either as its stored JSON string or already parsed. Anything that is not an array of
   * whole numbers yields an empty list rather than leaking a malformed value into the form.
   */
  public static parseIdArray(value: unknown): number[] {
    let parsed: unknown = value;

    if (typeof value === 'string') {
      try {
        parsed = JSON.parse(value);
      } catch {
        return [];
      }
    }

    return Array.isArray(parsed) ? parsed.filter((item): item is number => Number.isInteger(item)) : [];
  }

  /**
   * Serialises ids the way the API stores id-array columns: a JSON string such as "[12,13]", or
   * null when there is nothing to store. Never a bare id or a comma separated string.
   */
  public static toIdArrayJson(ids: number[]): string | null {
    const unique: number[] = [...new Set((ids ?? []).filter((id) => Number.isInteger(id)))];

    return unique.length ? JSON.stringify(unique) : null;
  }

  /**
   * `scw-mat-input` binds `[type]` dynamically, so Angular's NumberValueAccessor never applies and
   * `ngModel` hands back a string even for a numeric input. The API validates these columns with
   * `isInt`, so they are coerced on the way out; blank stays null rather than becoming NaN.
   */
  public static toNumberOrNull(value: unknown): number | null {
    if (value === null || value === undefined || String(value).trim() === '') {
      return null;
    }

    const parsed: number = Number(value);

    return Number.isFinite(parsed) ? parsed : null;
  }

  /**
   * Turns an API validation failure into something readable, so a rejected save says which field
   * the API refused and why instead of showing a raw payload dump.
   */
  public static formatApiError(error: unknown): string {
    const message: unknown = (error as { error?: { message?: unknown } })?.error?.message;

    if (Array.isArray(message)) {
      const lines: string[] = message
        .map((item: { property?: string; constraints?: Record<string, string> }) => {
          const constraints: string = Object.values(item?.constraints ?? {}).join(', ');

          return constraints || item?.property ? `${item?.property ?? ''}: ${constraints}`.trim() : '';
        })
        .filter(Boolean);

      if (lines.length) {
        return lines.join('\n');
      }
    }

    if (typeof message === 'string' && message) {
      return message;
    }

    const fallback: unknown = (error as { message?: unknown })?.message;

    return typeof fallback === 'string' && fallback ? fallback : JSON.stringify(error);
  }

  /**
   * `is_extended_enabled` is a three way mode, not a yes/no flag: 0 = off, 1 = change the activity and
   * wait, 2 = wait without changing the activity.
   */
  public static getExtendedModeOptions(translate: TranslateService): IApiAutomationOption[] {
    return [
      { id: EExtendedMode.no, name: translate.instant(`${TRANSLATION_PREFIX}.modal.isExtendedEnabled.no`) },
      {
        id: EExtendedMode.changeActivityAndWait,
        name: translate.instant(`${TRANSLATION_PREFIX}.modal.isExtendedEnabled.changeActivityAndWait`),
      },
      {
        id: EExtendedMode.doNotChangeActivityAndWait,
        name: translate.instant(`${TRANSLATION_PREFIX}.modal.isExtendedEnabled.doNotChangeActivityAndWait`),
      },
    ];
  }

  public static isExtendedModeOn(mode: unknown): boolean {
    return mode === EExtendedMode.changeActivityAndWait || mode === EExtendedMode.doNotChangeActivityAndWait;
  }

  /**
   * Tasks share titles across lines, so pickers label them "Line - Task". Needs the tasks request to
   * join `line||title`.
   */
  public static toTaskOption(task: {
    id: number | string;
    title: string;
    code?: string | null;
    line?: { title?: string } | null;
  }): IApiAutomationOption {
    const lineTitle: string | undefined = task.line?.title;
    const taskName: string = ApiAutomationSettingsUtilities.codeAndNameDisplayService.getDisplay({
      name: task.title,
      code: task.code,
    });

    return { id: Number(task.id), name: lineTitle ? `${lineTitle} - ${taskName}` : taskName };
  }

  /** Runtime activities carry no task, so their task pickers are emptied and greyed out. */
  public static getRunTimeActivityIds(activities: { id: number | string; activityType?: string }[]): Set<number> {
    return new Set<number>(
      (activities ?? [])
        .filter((activity) => activity.activityType === ActivityTypes.RUN_TIME)
        .map((activity) => Number(activity.id)),
    );
  }

  public static sortOptionsByName(options: IApiAutomationOption[]): IApiAutomationOption[] {
    return [...options].sort((first, second) => first.name.localeCompare(second.name));
  }

  /** Reads a comma separated id column such as `extended_cleaner_rule_ids` ("4,5"). */
  public static parseCommaIdList(value: unknown): number[] {
    if (value === null || value === undefined) {
      return [];
    }

    return String(value)
      .split(',')
      .map((part) => Number(part.trim()))
      .filter((id) => Number.isInteger(id) && id > 0);
  }

  /** Writes a comma separated id column ("4,5"), or null when there is nothing to store. */
  public static toCommaIdList(ids: number[]): string | null {
    const unique: number[] = [...new Set((ids ?? []).filter((id) => Number.isInteger(id) && id > 0))];

    return unique.length ? unique.join(',') : null;
  }

  public static stringifyJsonField(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }

    return typeof value === 'string' ? value : JSON.stringify(value);
  }
}
